import { Button, cn, Select, type SelectOption } from '@resolute/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, Search } from 'lucide-react';


import { type CatConfig, type CatField } from './category-config';

interface DynamicFilterBarProps {
  cat: CatConfig;
  values: Record<string, string>;
  setValue: (key: string, value: string) => void;
}

/** Prepend an enabled "Any …" option so an inline select can be cleared. */
const withAny = (field: CatField): SelectOption[] => [
  { value: '', label: field.anyLabel ?? `Any ${field.label.toLowerCase()}` },
  ...(field.options ?? []),
];

// Layout transition shared by every element that reflows on a category switch —
// a gentle spring so filters, and the Search action, glide to their new
// positions instead of jumping (prevents the flicker of a hard re-layout).
const GLIDE = { type: 'spring', stiffness: 500, damping: 40, mass: 0.6 } as const;

/**
 * Layer 2 — the dynamic filter bar. The set of controls, their labels, options
 * and count change with the selected category (rendered from config, animated
 * on switch).
 *
 * Layout: the filters and Search form one content-width row that centres in
 * the bar (`w-max` + `mx-auto`). The row is only as wide as its visible
 * controls — no fixed width, no reserved/placeholder columns — so Search sits
 * immediately after the last filter, with a uniform gap throughout. Fewer
 * filters ⇒ a narrower row that stays centred, never a fixed-width panel with
 * empty space before Search. When the row can't fit (narrow viewports) the
 * auto-margins collapse and it scrolls horizontally as a single line rather
 * than compressing.
 */
export function DynamicFilterBar({ cat, values, setValue }: DynamicFilterBarProps) {
  const reduce = useReducedMotion();
  // The row animates its *size* (`layout`) so its width eases smoothly as
  // filters are added/removed; the controls and the actions use
  // `layout="position"` so they reposition/glide without being stretched by the
  // parent's resize. All disabled under reduced motion.
  const layoutBox = reduce ? false : true;
  const layoutPos = reduce ? false : ('position' as const);

  const renderControl = (f: CatField) => {
    if (f.control === 'toggle') {
      const on = values[f.key] === 'true';
      return (
        <button
          type="button"
          aria-pressed={on}
          onClick={() => setValue(f.key, on ? '' : 'true')}
          className={cn(
            'inline-flex h-12 shrink-0 items-center gap-2 rounded-lg border px-3.5 text-sm shadow-sm transition-colors duration-base ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
            on
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-input bg-background text-muted-foreground hover:border-foreground/25',
          )}
        >
          <span
            className={cn(
              'flex size-4 items-center justify-center rounded-sm border',
              on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
            )}
            aria-hidden="true"
          >
            {on && <Check className="size-3" strokeWidth={3} />}
          </span>
          {f.label}
        </button>
      );
    }
    // select + budget both render as a compact native select. Full-width to
    // fill its grid cell below `lg`; a fixed compact width in the single-row
    // desktop layout at `lg` and up.
    return (
      <div className="w-full lg:w-[9.25rem] lg:shrink-0">
        <Select
          aria-label={f.label}
          options={withAny(f)}
          value={values[f.key] ?? ''}
          onChange={(e) => setValue(f.key, e.target.value)}
          className={cn('h-12', values[f.key] && 'border-primary')}
        />
      </div>
    );
  };

  return (
    /* Below `lg`: a plain 2-column grid that wraps to as many rows as the
     * category needs, with Search as its own full-width row — filters are
     * always fully visible and tappable, never require a horizontal swipe to
     * reach (a fixed-count grid handles a variable field count far more
     * predictably on a touch device than a scrolling single line does).
     * `layout` still animates the container smoothly between field counts —
     * here that means its height, as switching category changes row count.
     *
     * `lg` and up: the original single-row layout, centred and only as wide
     * as its visible controls, with the row's width animating smoothly as
     * filters change on category switch. The scroll/sticky-Search fallback
     * only matters if that row still can't fit a cramped desktop window. */
    <motion.div
      layout={layoutBox}
      transition={GLIDE}
      className="grid grid-cols-2 gap-3 lg:mx-auto lg:flex lg:w-max lg:items-center lg:gap-2 lg:overflow-x-auto lg:pb-1 lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden"
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {cat.fields.map((f) => (
          <motion.div
            key={`${cat.id}-${f.key}`}
            layout={layoutPos}
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
            transition={GLIDE}
            className="lg:shrink-0"
          >
            {renderControl(f)}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Search — its own full-width row spanning the grid below `lg`;
          immediately after the last filter (sticky to the scroll viewport's
          right edge as a fallback) in the single-row desktop layout. */}
      <motion.div
        layout={layoutPos}
        transition={GLIDE}
        className="col-span-2 flex items-center gap-2 lg:sticky lg:right-0 lg:col-span-1"
      >
        <Button type="submit" size="lg" className="w-full lg:w-auto lg:shrink-0">
          <Search className="size-4" aria-hidden="true" />
          Search
        </Button>
      </motion.div>
    </motion.div>
  );
}
